import React, { useEffect, useState, useCallback } from "react";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import moment from 'moment';
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDueOxyzo,
  fetchFilteredDueOxyzo
} from "../../../redux/slice/dueOxyzoReport";
import { downloadOxyzoReport } from '../../../redux/slice/collectionOxyzoReport';
import { HashLoader } from "react-spinners";
import { Card, Button, Input, Select, Space, Table, Alert, Empty, DatePicker } from "antd";
import { StyledTable, StyledButton } from "../components/styleComp";
import { useAuth } from "../../../context/AuthContext";
import jwt_decode from "jwt-decode";

const { Option } = Select;

const dueReport = (props) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [downloadOption, setDownloadOption] = useState('all');

  const { authState } = useAuth();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    pages: { dueOxyzo: { page: 1, pageSize: 7 } },
    filters: { dueOxyzo: {} },
    searchInputs: { dueOxyzo: "" },
    selectedColumns: { dueOxyzo: "" },
    hasSearched: { dueOxyzo: false },
  });

  const [triggerFilter, setTriggerFilter] = useState(null);

  const jwtBearer = authState?.token;
  const tokenDecoded = jwtBearer ? jwt_decode(jwtBearer) : null;
  const jwtPayload = tokenDecoded
    ? {
      group: props?.userDetails?.group_id,
      user: tokenDecoded?.user_id,
      role: props?.userDetails?.role,
      expiry: tokenDecoded?.exp,
    }
    : {};

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state?.pages?.dueOxyzo;
      dispatch(
        fetchDueOxyzo({
          jwtBearer,
          page,
          filters: state?.filters?.dueOxyzo,
          pageSize,
        })
      );
    }
  }, [jwtBearer, state?.pages?.dueOxyzo, state?.filters?.oxyzoCollection, dispatch]);

  const {
    data: dueOxyzoData = [],
    filteredData: filteredDueOxyzoData = [],
    totalCount: totalDueOxyzoCount,
    loading: isLoadingDueOxyzo,
    filteredLoading: isLoadingFilteredDueOxyzo,
    error: errorDueOxyzo,
  } = useSelector((state) => state?.oxyzoDue || {});


  const defineColumns = useCallback((data) => {
    if (!data || data?.length === 0) return [];
    const keys = Object.keys(data[0]);
    return keys.map((key) => ({
      title: <strong style={{ fontSize: "18px" }}>{key.replace(/_/g, " ").toUpperCase()}</strong>,
      dataIndex: key,
      key: key,
      render: (text) => <span style={{ fontSize: "16px" }}>{text}</span>,
    }));
  }, []);

  const handleKeyDown = (event, type) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleFilter(type);
    }
  };

  const handleFilter = (type) => {
    trackFeatureAction("Reports", "Due Report", "search");
    const columnName = state?.selectedColumns?.[type]?.toLowerCase();
    const trimmedInput = state?.searchInputs?.[type]?.trim();

    dispatch(
      fetchFilteredDueOxyzo({
        jwtBearer,
        filters: { [columnName]: trimmedInput },
      })
    );

    setState((prevState) => ({
      ...prevState,
      hasSearched: {
        ...prevState?.hasSearched,
        [type]: true,
      },
      pages: {
        ...prevState?.pages,
        [type]: { ...prevState?.pages?.[type], page: 1 },
      },
    }));
  };

  const handleReset = (type) => {
    trackFeatureAction("Reports", "Due Report", "reset");
    setState((prevState) => ({
      ...prevState,
      searchInputs: {
        ...prevState?.searchInputs,
        [type]: "",
      },
      selectedColumns: {
        ...prevState?.selectedColumns,
        [type]: "",
      },
      hasSearched: {
        ...prevState?.hasSearched,
        [type]: false,
      },
      filters: {
        ...prevState?.filters,
        [type]: {},
      },
    }));
    setTriggerFilter(type);
  };

  const paginationConfig = (type) => {
    const total = state?.hasSearched?.dueOxyzo
      ? filteredDueOxyzoData?.length
      : totalDueOxyzoCount;

    return {
      current: state?.pages?.dueOxyzo?.page,
      pageSize: state?.pages?.dueOxyzo?.pageSize,
      total: total,
      showTotal: (total, [start, end]) => `${start}-${end} of ${total} items`,
      onChange: (page, pageSize) =>
        setState((prevState) => ({
          ...prevState,
          pages: { ...prevState?.pages, [type]: { page, pageSize } },
        })),
    };
  };

  const renderSearchBar = (columns, type) => (
    <Space style={{ marginBottom: 16, marginLeft: 2 }}>
      <Select
        placeholder="Select Column"
        style={{ width: 200, height: 32, lineHeight: "32px" }}
        value={state?.selectedColumns?.[type] || undefined}
        onChange={(value) =>
          setState((prevState) => ({
            ...prevState,
            selectedColumns: {
              ...prevState?.selectedColumns,
              [type]: value,
            },
          }))
        }
      >
        {columns?.map((col) => (
          <Option key={col?.key} value={col?.dataIndex}>
            {col?.title?.props?.children}
          </Option>
        ))}
      </Select>
      <Input
        placeholder="Value"
        value={state?.searchInputs?.[type]}
        onChange={(e) =>
          setState((prevState) => ({
            ...prevState,
            searchInputs: {
              ...prevState?.searchInputs,
              [type]: e.target.value,
            },
          }))
        }
        onKeyDown={(e) => handleKeyDown(e, type)}
        style={{ width: 150, height: 32, lineHeight: "32px" }}
        disabled={!state?.selectedColumns?.[type]}
      />
      <Button
        type="primary"
        onClick={() => handleFilter(type)}
        style={{ height: 32, lineHeight: "32px" }}
      >
        Search
      </Button>
      {state?.hasSearched?.dueOxyzo && (
        <Button
          onClick={() => handleReset(type)}
          style={{ width: 70, height: 32, lineHeight: "32px" }}
        >
          Reset
        </Button>
      )}
    </Space>
  );

  const renderButtonByType = () => (
    <StyledButton
      type="primary"
      onClick={() => handleAPIDownload()}
      loading={buttonLoading}
      disabled={downloadOption === 'dateRange' && (!startDate || !endDate)}
    >
      Download Report
    </StyledButton>
  );

  // const handleAPIDownload = () => {
  //   console.log("in handle api");
  //   setButtonLoading(true);

  //   try {
  //     dispatch(downloadOxyzoReport({ jwtBearer,startDate: "", endDate: "",type:"collection", type: "due" }))

  //   }
  //   catch (error) {
  //     console.log("error", error);
  //   }
  //   finally {
  //     setButtonLoading(false);
  //   }

  // };

const handleAPIDownload = () => {
  trackFeatureAction("Reports", "Due Report", "download");
  console.log("in handleAPIDownload");
  setButtonLoading(true);
  dispatch(downloadOxyzoReport({ jwtBearer, startDate: "", endDate: "", type: "due" }))
    .then(() => setButtonLoading(false))
    .catch(() => setButtonLoading(false));
};


  return (
    <Card
      title={<span style={{ fontWeight: 600, fontSize: "24px", fontFamily: "poppins-font, sans-serif" }}>DUE REPORT</span>}
      bordered
      extra={
        <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <StyledButton
              type="primary"
              onClick={handleAPIDownload}
              loading={buttonLoading}
            >
              Download Report
            </StyledButton>
          </div>
        </div>
      }
    >
      {renderSearchBar(defineColumns(dueOxyzoData || []), "dueOxyzo")}

      {(isLoadingFilteredDueOxyzo || isLoadingDueOxyzo) ? (
        <div className="spinner-container">
          <HashLoader color="#f57171" loading={true} />
        </div>
      ) : errorDueOxyzo ? (
        <Alert message={errorDueOxyzo} type="error" />
      ) : (
        <StyledTable
          columns={defineColumns(dueOxyzoData)}
          dataSource={
            state?.hasSearched?.dueOxyzo
              ? filteredDueOxyzoData
              : dueOxyzoData
          }
          pagination={paginationConfig("dueOxyzo")}
          locale={{ emptyText: <Empty description="No data available" /> }}
        />
      )}
    </Card>
  );
};

export default dueReport;